<template>
  <div class="waveform-table-base">
    <!-- 波形查看弹窗 -->
    <el-dialog 
      v-if="waveDialogVisible" 
      title="负载识别波形" 
      :visible.sync="waveDialogVisible" 
      width="95%"
      top="1.8vh"
      :before-close="handleWaveClose" 
      :close-on-click-modal="true"
      :modal="false"
      :lock-scroll="false">
      <div class="fault_box">
        <!-- 设备信息区域 -->
        <div v-if="currentWaveRow" class="device-info-section">
          <!-- 设备信息 -->
          <div class="info-row">
            <div class="info-item">
              <span class="info-label">名称：</span>
              <span class="info-value">{{ getDisplayName(currentWaveRow) || '-' }}</span>
            </div>
            <div v-if="showTriggerTime" class="info-item">
              <span class="info-label">触发时间：</span>
              <span class="info-value">{{ getDisplayTime(currentWaveRow) || '-' }}</span>
            </div>
            <!-- 导航按钮区域 -->
            <div class="navigation-item">
              <el-button 
                size="mini" 
                :disabled="!hasPrevious" 
                @click="handlePrevious"
                icon="el-icon-arrow-left"
                class="nav-btn">
                上一条
              </el-button>
              <span class="navigation-info">
                {{ currentWaveIndex + 1 }} / {{ tableData.length }}
              </span>
              <el-button 
                size="mini" 
                :disabled="!hasNext" 
                @click="handleNext"
                icon="el-icon-arrow-right"
                class="nav-btn">
                下一条
              </el-button>
            </div>
          </div>
        </div>
        
        <!-- 波形图区域 -->
        <wave-chart v-if="waveData" :id="currentWaveId" :wave-data="waveData"></wave-chart>
        <div v-else-if="waveLoading" class="loading-tip">
          <i class="el-icon-loading"></i>
          <span>正在加载波形数据...</span>
        </div>
      </div>
    </el-dialog>
    
    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="view-search-form" size="small">
      <el-form-item 
        v-for="item in searchConfig" 
        :key="item.prop"
        :label="item.label">
        <!-- 输入框 -->
        <el-input
          v-if="item.type === 'input'"
          v-model="searchForm[item.prop]"
          :placeholder="item.placeholder"
          clearable
          :style="{width: item.width}">
        </el-input>
        <!-- 选择框 -->
        <el-select
          v-else-if="item.type === 'select'"
          v-model="searchForm[item.prop]"
          :placeholder="item.placeholder"
          clearable
          :style="{width: item.width}">
          <el-option
            v-for="option in item.options"
            :key="option.value"
            :label="option.label"
            :value="option.value">
          </el-option>
        </el-select>
        <!-- 日期时间范围 -->
        <el-date-picker
          v-else-if="item.type === 'datetimerange'"
          v-model="searchForm[item.prop]"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          :format="item.format"
          :value-format="item.valueFormat"
          :style="{width: item.width}">
        </el-date-picker>
      </el-form-item>
      <el-form-item class="view-button-group">
        <div class="button-wrapper">
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button @click="handleReset" icon="el-icon-refresh">重置</el-button>
        </div>
      </el-form-item>
    </el-form>
    
    <!-- 批量操作按钮区域 -->
    <div class="batch-operation-area" style="margin-bottom: 10px;">
      <el-button 
        @click="handleBatchMigrate" 
        type="primary"
        :disabled="selectedRows.length === 0"
        size="mini">
        批量迁移至
      </el-button>
      <span v-if="selectedRows.length > 0" style="margin-left: 10px;">
        已选择 
        <span style="color: #3fadb8;font-weight: 900;">{{ selectedRows.length }}</span>
        项
      </span>
    </div>

    <!-- 数据表格 -->
    <el-table
      :ref="tableRef"
      :data="tableData"
      v-loading="tableLoading"
      border
      max-height="450px"
      style="width: 100%"
      :header-cell-style="{ backgroundColor: '#fafafa', color: '#606266', fontWeight: 600, fontSize: '14px', padding: '12px 0' }"
      @selection-change="handleSelectionChange">
      <el-table-column
        type="selection"
        width="55"
        align="center"
        :selectable="selectableRow">
      </el-table-column>
      <el-table-column
        v-for="column in tableColumns"
        :key="column.prop"
        :prop="column.prop"
        :label="column.label"
        :width="column.width"
        :align="column.align"
        :render-header="column.isStatusColumn ? renderStatusHeader : null">
        <template slot-scope="scope" >
          <!-- 状态列 -->
          <span v-if="column.isStatusColumn" :class="['status-tag', getStatusInfo(scope.row.processingStatus, scope.row.failReason).class]">
            {{ getStatusInfo(scope.row.processingStatus, scope.row.failReason).text }}
          </span>
          <!-- 名称列 -->
          <span v-else-if="isNameColumn(column)" 
                :title="getDisplayName(scope.row)" 
                class="name-cell">
            <!-- 如果有obsPath，显示为可点击的下载链接 -->
            <a v-if="scope.row.obsPath" 
               href="javascript:void(0);" 
               class="download-link"
               :title="getDisplayName(scope.row)"
               @click.stop="handleDownload(scope.row)">
              <i class="el-icon-download" style="margin-right: 4px; color: #3fadb8; flex-shrink: 0;"></i>
              <span class="download-text">{{ getDisplayName(scope.row) }}</span>
            </a>
            <!-- 如果没有obsPath，只显示文本 -->
            <span v-else class="name-text">{{ getDisplayName(scope.row) }}</span>
          </span>
          <!-- 其他列 -->
          <span v-else>{{ scope.row[column.prop] }}</span>
        </template>
      </el-table-column>
      <el-table-column
        label="操作"
        align="center">
        <template slot-scope="scope">
          <template v-for="btn in tableOperations">
            <el-button
              v-if="1"
              :key="btn.label"
              :type="btn.type"
              :size="btn.size"
              :disabled="(btn.label === '查看波形' && scope.row.processingStatus !== 2) || (btn.label === '迁移至' && scope.row.processingStatus !== 2)"
              @click="handleOperation(btn.handler, scope.row)">
              {{ btn.label }}
            </el-button>
          </template>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页组件 -->
    <div class="view-pagination" v-if="tableData.length > 0">
      <PageButton
        :total="pageData.total"
        :page-size="pageData.pageSize"
        :current-page="pageData.currentPage"
        @pageChange="handleCurrentChange"
        @sizeChange="handleSizeChange">
      </PageButton>
    </div>
  </div>
</template>

<script>
import PageButton from '@/components/buttons/PageButton.vue'
import waveChart from '@/view/waveform/load/components/waveChart.vue'

export default {
  name: 'WaveformTableBase',
  components: {
    PageButton,
    waveChart
  },
  props: {
    // 表格引用名称
    tableRef: {
      type: String,
      default: 'waveformTable'
    },
    // 标签ID
    labelId: {
      type: [String, Number, null],
      default: null
    },
    // 字典项ID
    dictItemId: {
      type: [String, Number, null],
      default: null
    },
    // 搜索配置
    searchConfig: {
      type: Array,
      required: true
    },
    // 表格列配置
    tableColumns: {
      type: Array,
      required: true
    },
    // 表格操作按钮配置
    tableOperations: {
      type: Array,
      default: () => [
        {
          label: '查看波形',
          type: 'text',
          size: 'mini',
          handler: 'handleView'
        },
        {
          label: '迁移至',
          type: 'text',
          size: 'mini',
          handler: 'handleMigrateVisible'
        }
      ]
    },
    // 批量操作类型
    batchType: {
      type: String,
      default: 'import'
    },
    // 是否显示触发时间（用于区分不同Tab的显示需求）
    showTriggerTime: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 表格数据
      tableData: [],
      tableLoading: false,
      selectedRows: [],
      
      // 搜索表单
      searchForm: {},
      
      // 分页数据
      pageData: {
        currentPage: 1,
        pageSize: 20,
        total: 0
      },
      
      // 波形查看相关数据
      waveDialogVisible: false,
      currentWaveId: null,
      currentWaveRow: null,
      currentWaveIndex: -1,
      waveData: null,
      waveLoading: false
    }
  },
  computed: {
    // 是否有上一条记录
    hasPrevious() {
      return this.currentWaveIndex > 0;
    },
    
    // 是否有下一条记录
    hasNext() {
      return this.currentWaveIndex >= 0 && this.currentWaveIndex < this.tableData.length - 1;
    }
  },
  watch: {
    // 监听labelId变化，重新加载数据
    labelId: {
      immediate: true,
      handler() {
        this.initSearchForm();
        this.loadData();
      }
    },
    // 监听搜索配置变化，重新初始化搜索表单
    searchConfig: {
      immediate: true,
      handler() {
        this.initSearchForm();
      }
    }
  },
  methods: {
    // 初始化搜索表单
    initSearchForm() {
      const form = {};
      this.searchConfig.forEach(item => {
        if (item.type === 'datetimerange') {
          form[item.prop] = null;
        } else if (item.type === 'select' && item.options && item.options[0]) {
          form[item.prop] = item.options[0].value;
        } else {
          form[item.prop] = '';
        }
      });
      this.searchForm = form;
    },

    // 判断是否为名称列
    isNameColumn(column) {
      return column.prop === 'name' || column.prop === 'macAddr';
    },

    // 获取显示名称
    getDisplayName(row) {
      // 子组件可以覆盖此方法来自定义名称显示逻辑
      if (row.name) {
        return row.name;
      }
      // 如果是 MigrateTab 的数据格式（有 macAddr 和 triggerTime）
      if (row.macAddr && row.triggerTime) {
        return this.formatFileName(row);
      }
      return this.formatFileName(row);
    },

    // 获取显示时间
    getDisplayTime(row) {
      // 子组件可以覆盖此方法来自定义时间显示逻辑
      return row.createTime || row.triggerTime;
    },

    // 格式化文件名（默认实现，子组件可覆盖）
    formatFileName(row) {
      // 如果有 macAddr 和 triggerTime，使用 MigrateTab 的格式
      if (row.macAddr && row.triggerTime) {
        const cleanTime = row.triggerTime.replace(/[-:\s]/g, '');
        return `${row.macAddr}_${cleanTime}.npy`;
      }
      // 否则使用默认格式
      return row.name || '';
    },

    // 获取状态文本和样式
    getStatusInfo(status, failReason) {
      const statusMap = {
        1: { text: '未处理', class: 'status-pending' },
        2: { text: '处理完成', class: 'status-success' },
        3: { text: '处理失败：', class: 'status-failed' }
      }
      const statusInfo = statusMap[status] || { text: '未知状态', class: 'status-unknown' }
      
      // 如果是处理失败且存在失败原因，则显示状态+原因
      if (status === 3 && failReason) {
        return {
          text: `${statusInfo.text} (${failReason})`,
          class: statusInfo.class
        }
      }
      
      return statusInfo
    },

    // 渲染状态列的自定义表头
    renderStatusHeader(h, { column }) {
      return h('div', {
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }
      }, [
        h('span', column.label),
        h('el-tooltip', {
          props: {
            content: '刷新列表状态数据',
            placement: 'top',
            effect: 'light'
          }
        }, [
          h('el-button', {
            props: {
              type: 'primary',
              size: 'mini',
              icon: 'el-icon-refresh',
              loading: this.tableLoading,
              circle: true
            },
            on: {
              click: this.handleRefreshStatus
            },
            style: {
              padding: '3px',
              marginLeft: '6px',
              fontSize: '14px'
            }
          })
        ])
      ])
    },

    // 刷新状态数据
    handleRefreshStatus() {
      this.loadData()
    },

    // 处理表格选择变化
    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    // 判断行是否可选
    selectableRow(row) {
      return row.processingStatus == 2;
    },

    // 搜索
    handleSearch() {
      this.pageData.currentPage = 1
      this.loadData()
    },

    // 重置
    handleReset() {
      this.initSearchForm();
      this.pageData.currentPage = 1;
      this.loadData();
    },

    // 批量迁移
    handleBatchMigrate() {
      if (this.selectedRows.length === 0) {
        this.$message({
          message: "请选择要迁移的记录",
          type: "warning",
        });
        return;
      }
      const recordIds = this.selectedRows.map((row) => row.id);
      this.$emit('batch-migrate', recordIds, this.batchType);
    },

    // 处理表格操作
    handleOperation(handler, row) {
      if (handler === 'handleView') {
        this.handleViewWave(row.id);
      } else if (handler === 'handleMigrateVisible') {
        // 检查处理状态
        if (row.processingStatus === 1) {
          // 未处理状态，给出warning提示
          this.$message({
            message: '未处理的数据禁止迁移',
            type: 'warning'
          });
          return;
        } else if (row.processingStatus === 2) {
          // 处理完成状态，正常迁移
          this.$emit('migrate', [row.id], this.batchType);
        }
      }
    },

    // 查看波形
    async handleViewWave(id) {
      // 查找当前行数据
      const rowIndex = this.tableData.findIndex(row => row.id === id);
      if (rowIndex === -1) return;
      
      this.currentWaveId = id;
      this.currentWaveRow = this.tableData[rowIndex];
      this.currentWaveIndex = rowIndex;
      this.waveDialogVisible = true;
      this.waveLoading = true;
      this.waveData = null;
      
      // 触发查看波形事件，让父组件处理具体的API调用
      this.$emit('view-wave', id, (waveData) => {
        if (waveData) {
          this.$nextTick(() => {
            this.waveData = waveData;
            this.waveLoading = false;
          });
        } else {
          this.waveDialogVisible = false;
          this.waveLoading = false;
        }
      });
    },

    // 切换到上一条记录
    handlePrevious() {
      if (this.hasPrevious) {
        const previousIndex = this.currentWaveIndex - 1;
        const previousRow = this.tableData[previousIndex];
        this.handleViewWave(previousRow.id);
      }
    },
    
    // 切换到下一条记录
    handleNext() {
      if (this.hasNext) {
        const nextIndex = this.currentWaveIndex + 1;
        const nextRow = this.tableData[nextIndex];
        this.handleViewWave(nextRow.id);
      }
    },

    // 关闭波形弹窗
    handleWaveClose() {
      this.waveDialogVisible = false;
      this.waveData = null;
      this.currentWaveId = null;
      this.currentWaveRow = null;
      this.currentWaveIndex = -1;
      // 触发关闭事件
      this.$emit('wave-close');
    },

    // 下载文件
    async handleDownload(row) {
      // 触发下载事件，让父组件处理具体的下载逻辑
      this.$emit('download', row);
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pageData.pageSize = val
      this.pageData.currentPage = 1
      this.loadData()
    },

    // 当前页改变
    handleCurrentChange(val) {
      this.pageData.currentPage = val
      this.loadData()
    },

    // 加载数据
    loadData() {
      // 设置加载状态
      this.tableLoading = true;
      
      // 触发加载数据事件，让父组件处理具体的数据加载逻辑
      this.$emit('load-data', {
        searchForm: this.searchForm,
        pageData: this.pageData,
        labelId: this.labelId,
        dictItemId: this.dictItemId
      }, (data, total) => {
        this.tableData = data || [];
        this.pageData.total = total || 0;
        // 数据加载完成后关闭加载状态
        this.tableLoading = false;
      });
    },

    // 设置加载状态
    setLoading(loading) {
      this.tableLoading = loading;
    }
  }
}
</script>

<style scoped>
.waveform-table-base {
  padding: 0;
}

/* 搜索表单样式 */
.view-search-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.view-button-group {
  margin-left: auto;
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.button-wrapper {
  display: flex;
  gap: 8px;
}

/* 分页样式 */
.view-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: right;
}

/* 状态标签样式 */
.status-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
}

.status-pending {
  color: #f2a606;
}

.status-success {
  color: #52c41a;
}

.status-failed {
  color: #ff4d4f;
}

.status-unknown {
  color: #999;
}

/* 名称列样式 */
.name-cell {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

/* 下载链接样式 */
.download-link {
  color: #3fadb8;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
}

/* 下载链接文本样式 */
.download-text {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 普通名称文本样式 */
.name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.download-link:hover {
  color: #5fc3cc;
  text-decoration: underline;
}

.download-link:active {
  transform: translateY(1px);
}

/* 下载图标动画 */
.download-link i {
  transition: transform 0.3s ease;
}

.download-link:hover i {
  transform: translateY(2px);
}

/* 下载中状态样式 */
.download-loading {
  color: #b1b3b8;
  cursor: not-allowed;
}

.download-loading i {
  animation: downloading 1s linear infinite;
}

@keyframes downloading {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0px);
  }
}

/* 设备信息区域样式 */
.device-info-section {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* 设备信息样式 */
.info-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px 40px;
}

.info-item {
  display: flex;
  align-items: center;
  min-width: 200px;
}

.info-label {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  margin-right: 8px;
  white-space: nowrap;
}

.info-value {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
  background: #ffffff;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  min-width: 120px;
  text-align: center;
}

/* 导航按钮区域样式 */
.navigation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.nav-btn {
  min-width: 80px;
}

.navigation-info {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  min-width: 60px;
  text-align: center;
}

.el-button:disabled{
  opacity: 0.4;
}

/* 波形弹窗样式 */
.fault_box {
  min-height: 350px;
}

.loading-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #909399;
  font-size: 14px;
}

.loading-tip i {
  font-size: 24px;
  margin-right: 8px;
}

.loading-tip i {
  color: #00d8c3;
  animation: rotating 2s linear infinite;
}

/* 状态列刷新按钮样式 */
.status-refresh-btn {
  padding: 0;
  margin-left: 4px;
  color: #606266;
  transition: color 0.3s ease;
}

.status-refresh-btn:hover {
  color: #3fadb8;
}

.status-refresh-btn.is-loading {
  color: #3fadb8;
}

/* 状态列刷新按钮悬停效果 */
.el-button--primary.status-refresh-btn:hover {
  background-color: #5fb9c5;
  border-color: #5fb9c5;
}

.el-button--primary.status-refresh-btn:focus {
  background-color: #3fadb8;
  border-color: #3fadb8;
}

/* 刷新按钮动画效果 */
.status-refresh-btn .el-icon-refresh {
  transition: transform 0.3s ease;
}

.status-refresh-btn:hover .el-icon-refresh {
  transform: rotate(180deg);
}

.status-refresh-btn.is-loading .el-icon-refresh {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>